import { motion } from "framer-motion";

import { formatTimeAmPm } from "../lib/timeUtils";

const getLojoMeta = (time) => {
  const timeRaw = time?.toString()?.trim() ?? "";
  const hhmm = timeRaw.length >= 5 ? timeRaw.slice(0, 5) : timeRaw;
  const [hours, minutes] = hhmm.split(":").map((part) => parseInt(part, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return { label: "FN", badge: "bg-slate-500/90 text-white" };
  }
  if (hours < 12) {
    return { label: "FN", badge: "bg-amber-400/95 text-amber-950" };
  }
  if (hours < 15) {
    return { label: "AN", badge: "bg-emerald-400/95 text-emerald-950" };
  }
  return { label: "EVG", badge: "bg-sky-400/95 text-slate-950" };
};

// Dark slot grid: available / selected / booked.
export default function SlotPicker({ slots, value, onChange, loading, onLeave = false, leaveReason = "" }) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="slots-loading">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }
  if (onLeave) {
    return (
      <div data-testid="doctor-on-leave" role="status" aria-live="polite" className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-6 text-center text-amber-100">
        <p className="font-semibold">Doctor is on leave for this date.</p>
        {leaveReason.trim() && <p className="mt-2 text-sm text-amber-100/80">Reason: {leaveReason.trim()}</p>}
      </div>
    );
  }
  if (!slots || slots.length === 0) {
    return (
      <div data-testid="slots-empty" className="rounded-2xl border border-dashed border-border bg-muted/40 p-8 text-center text-muted-foreground">
        No slots available for this day. Please try another date.
      </div>
    );
  }
  const uniqueSlots = Array.from(new Map((slots || []).map((s) => {
    const time = (s.time ?? s.slotTime ?? "").toString().slice(0, 5);
    return [time || `__slot-${Math.random()}`, s];
  })).values());

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="slot-grid">
      {uniqueSlots.map((s, index) => {
        const time = s.time ?? s.slotTime ?? "";
        const blocked = Boolean(s.blocked ?? s.booked ?? s.isBooked ?? false);
        const selected = value === time;
        const key = s.id ?? `${time}-${index}`;
        const { label, badge } = getLojoMeta(time);
        const displayTime = formatTimeAmPm(time);

        if (blocked) {
          return (
            <div
              key={key}
              data-testid={`slot-${time}`}
              aria-disabled="true"
              className="relative overflow-hidden rounded-xl py-3 text-center font-medium text-sm bg-muted/30 text-muted-foreground/65 border border-border cursor-not-allowed line-through opacity-80 pointer-events-none"
            >
              <div className="pointer-events-none absolute left-0 top-0 z-10">
                <div className={`absolute left-0 top-0 w-8 h-8 ${badge}`} style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }} />
                <div className="absolute left-0 top-0 w-8 h-8 flex items-start justify-start p-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[.25em] text-white leading-none">{label}</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2">
                <span>{displayTime}</span>
              </div>
            </div>
          );
        }

        return (
          <motion.button
            key={key}
            type="button"
            data-testid={`slot-${time}`}
            whileTap={{ scale: 0.96 }}
            onClick={() => onChange(time)}
            className={`relative overflow-hidden rounded-xl py-3 text-center font-medium text-sm border transition-colors ${
              selected
                ? "aurora-gradient text-white border-transparent shadow-[0_0_18px_rgba(124,58,237,0.4)]"
                : "bg-muted/50 text-foreground/90 border-border hover:border-primary hover:text-primary"
            }`}
          >
            <div className="pointer-events-none absolute left-0 top-0 z-10">
              <div className={`absolute left-0 top-0 w-8 h-8 ${badge}`} style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }} />
              <div className="absolute left-0 top-0 w-8 h-8 flex items-start justify-start p-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-[.25em] text-white leading-none">{label}</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span>{displayTime}</span>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}
